import React from 'react';
import styled from 'styles/styled';
import iconClose from '../../../assets/Icon/icon_close.svg';

const CalendarSvg = styled.svg`
  width: 16px;
  height: 16px;
  margin-right: 8px;
  flex-shrink: 0;
`;

/**
 * Inline calendar icon — uses currentColor so the parent label's color
 * (which switches to green when the radio is selected) cascades into the
 * stroke. Replaces the previous raster background-image approach.
 */
export const IconCalendar: React.FC = () => (
  <CalendarSvg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={1.8}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </CalendarSvg>
);

export const ErrorText = styled.p`
  color: #FF6782;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
`;

/**
 * Outline circular info badge — matches design's `.section__info`.
 * Renders an "i" via ::before so callers can keep using `<IconInfo onClick={...} />`.
 */
export const IconInfo = styled.span`
  width: 14px;
  height: 14px;
  margin-top: 1px;
  margin-left: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid #7A7E8C;
  color: #7A7E8C;
  font-size: 9px;
  line-height: 12px;
  font-weight: 700;
  cursor: pointer;
  font-family: inherit;
  user-select: none;

  &::before {
    content: 'i';
  }

  &:hover {
    color: #EFEFEF;
    border-color: #EFEFEF;
  }
`;

export const RowText = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 10px;
`;

export const Title = styled.h2`
  font-size: 28px;
  font-weight: 700;
  line-height: 1.2;
  margin-bottom: 6px;
  text-align: center;
  color: #EFEFEF;
  letter-spacing: -0.01em;
  @media (max-width: 720px) {
    font-size: 24px;
    margin-bottom: 4px;
  }
`;

export const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  button.btnSubmit {
    margin-top: 28px;
    width: 100%;
    min-width: 0;
    min-height: 54px;
    padding: 16px 22px;
    font-size: 18px;
    font-weight: 700;
    letter-spacing: 0.01em;
    color: #0A0A0A;
    border-radius: 10px;
    transition: background-color 200ms ease-in, box-shadow 200ms ease-in;

    &:hover {
      background-color: #3FB46A;
      opacity: 1;
    }
    &:focus-visible {
      outline: none;
      box-shadow: 0 0 0 4px rgba(82, 211, 129, 0.28);
    }
    &:disabled {
      background-color: #535D70;
      color: #1F2329;
    }
    @media (max-width: 720px) {
      height: 54px;
      min-height: 54px;
      border-radius: 10px;
      font-size: 18px;
    }
  }
`;

export const Text = styled.p`
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: #A7A8B2;
  margin: 0;
  letter-spacing: 0.01em;
  &.center {
    text-align: center;
    font-size: 14px;
    color: #EFEFEF;
    font-weight: 500;
    margin: -2px 0 12px;
  }
  &.inner {
    font-size: 14px;
    color: #EFEFEF;
    margin-bottom: 0;
  }
`;

export const TextBold = styled.p`
  font-size: 14px;
  font-weight: 300;
  line-height: 18px;
  color: #A7A8B2;
  margin: 0 0 22px;
  text-align: center;
  span {
    color: #52D381;
    font-weight: 500;
  }
  @media (min-width: 768px) {
    margin-bottom: 28px;
  }
`;

export const Content = styled.div`
  margin-top: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  label {
    font-size: 16px;
    font-weight: 500;
    line-height: 20px;
    color: #EFEFEF;
    background-color: #292B2E;
    min-height: 50px;
    padding: 8px 14px;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    border-radius: 10px;
    border: 1px solid #34373C;
    cursor: pointer;
    transition: border-color 200ms ease-in, color 200ms ease-in;
  }
  label:hover {
    border-color: #5C6168;
  }
  input[type='radio'] {
    display: none;
    &:checked + label {
      color: #52D381;
      border-color: #52D381;
    }
  }
`;

export const Row = styled.div`
  margin-top: 10px;
  margin-bottom: 22px;
  display: flex;
  gap: 10px;
  width: 100%;
  max-width: 100%;
  justify-content: space-between;
  @media (min-width: 768px) {
    margin-bottom: 26px;
  }
  @media (max-width: 720px) {
    min-width: 264px;
    width: 100%;
  }
  &.RowLarge {
    flex-wrap: wrap;
    margin-bottom: 12px;
    @media (min-width: 768px) {
      margin-bottom: 16px;
    }
  }
  &.customRow {
    width: 320px;
    margin-left: auto;
    margin-right: auto;
    justify-content: center;
  }
`;

export const Col = styled.div`
  flex: 1 1 0;
  min-width: 0;
  position: relative;
  /**
   * Disabled radio-segment styling per
   * trading-battles-design-system/project/preview/components-inputs.html
   * (.field.disabled — bg #1B1F25, color #535D70, cursor not-allowed)
   * adapted to the segment-button shell. Replaces the previous
   * opacity: 0.4 blend with token-driven values so the disabled
   * "1vs1" tile reads consistently against DS-2 disabled state.
   */
  &.disabled {
    cursor: not-allowed;
    label {
      background-color: #1B1F25;
      border-color: #535D70;
      color: #535D70;
      cursor: not-allowed;
      pointer-events: none;
    }
    label:hover {
      border-color: #535D70;
    }
  }
`;

export const ColMaxW = styled.div`
  flex: 1 1 calc(33.333% - 7px);
  min-width: calc(33.333% - 7px);
  margin-bottom: 10px;
`;

export const TimePickerRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 16px;
  margin-bottom: 8px;
`;

export const TimeInput = styled.input`
  width: 56px;
  height: 40px;
  background: #292B2E;
  border: 1px solid #292B2E;
  border-radius: 12px;
  color: #EFEFEF;
  font-size: 24px;
  font-weight: 500;
  text-align: center;
  outline: none;
  transition: border-color 200ms ease-in, box-shadow 200ms ease-in;
  -moz-appearance: textfield;
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
  &:focus {
    border-color: #52D381;
    box-shadow: 0 0 0 4px rgba(82, 211, 129, 0.18);
  }
`;

export const TimeColon = styled.span`
  font-size: 24px;
  font-weight: 500;
  color: #7A7E8C;
  margin: 0 8px;
`;

export const UTCLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #52D381;
  margin-left: 12px;
`;

export const LocalTimeHint = styled.p`
  font-size: 12px;
  color: #7A7E8C;
  text-align: center;
  margin-bottom: 16px;
`;

export const DatetimePreview = styled.p`
  font-size: 14px;
  color: #EFEFEF;
  text-align: center;
  margin-bottom: 8px;
`;

export const CalendarWrap = styled.div`
  max-height: calc(100vh - 120px);
  overflow-y: auto;
  button.center {
    margin: 0 auto;
    min-width: 168px;
  }
  p {
    text-align: center;
    margin-bottom: 19px;
  }
  .react-calendar__navigation button:disabled {
    background-color: transparent;
    color: #696969;
  }
  .react-calendar__navigation button:enabled:hover, .react-calendar__navigation button:enabled:focus {
    background-color: transparent;
  }
  .react-calendar__navigation__label {
    pointer-events: none;
  }
  .react-calendar {
    margin-bottom: 32px;
    padding: 12px 8px 8px 8px;
    border: none;
    background-color: #292B2E;
    border-radius: 12px;
    color: #fff;
    font-size: 10px;
    font-weight: 400;
    line-height: 12px;
    letter-spacing: 1.5px;
    .react-calendar__navigation {
      height: inherit;
      margin-bottom: 7px;
    }
    .react-calendar__month-view__weekdays {
      margin-bottom: 22px;
    }
    .react-calendar__month-view__weekdays {
      abbr {
        text-decoration: none;
        color: #A7A8B2;
        font-size: 10px;
        font-weight: 400;
        line-height: 12px;
        letter-spacing: 1.5px;
      }
    }
    button {
      color: #fff;
      font-size: 16px;
      font-weight: 400;
      line-height: 18px;
      height: 30px;
      width: 30px;
      padding: 0;
      margin-bottom: 8px;
    }
    .react-calendar__tile:enabled:hover, .react-calendar__tile:enabled:focus {
      background-color: transparent;
      abbr {
        width: 30px;
        height: 30px;
        display: flex;
        border-radius: 100px;
        border: 1px solid #52D381;
        align-items: center;
        justify-content: center;
        margin: 0 auto;
        color: #52D381;
      }
    }
    .react-calendar__tile--now {
      background-color: transparent;
      color: #52D381;
    }
    .react-calendar__tile--active {
      background-color: transparent;
      abbr {
        width: 30px;
        height: 30px;
        display: flex;
        border-radius: 100px;
        background-color: #52D381;
        align-items: center;
        justify-content: center;
        margin: 0 auto;
        color: #000000 !important;
      }
    }
    .react-calendar__navigation__prev2-button,
    .react-calendar__navigation__next2-button {
      display: none;
    }
    .react-calendar__tile:disabled {
      background-color: #292B2E;
      color: #7A7E8C;
    }
  }
`;

export const Close = styled.div`
  background-image: url(${iconClose});
  background-repeat: no-repeat;
  background-position: center;
  cursor: pointer;
  position: absolute;
  right: 0;
  top: -56px;
  color: #fff;
  width: 40px;
  height: 40px;
  background-color: #292B2E;
  border: 1px solid #34373C;
  border-radius: 999px;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: background-color 200ms ease-in, border-color 200ms ease-in;
  &:hover {
    background-color: #1B1F25;
    border-color: #5C6168;
  }
`;

export const ComingSoonTooltip = styled.div`
  position: absolute;
  bottom: calc(100% + 8px);
  left: 50%;
  transform: translateX(-50%);
  background: #292B2E;
  color: #EFEFEF;
  padding: 6px 12px;
  border-radius: 8px;
  font-size: 12px;
  white-space: nowrap;
  border: 1px solid #696969;
  z-index: 10;
  animation: fadeInOut 2.5s ease-in-out;

  @keyframes fadeInOut {
    0% { opacity: 0; }
    10% { opacity: 1; }
    80% { opacity: 1; }
    100% { opacity: 0; }
  }
`;
